/** */
import React from 'react';
import { Icon } from 'assets/icons';
import { SectionCard } from 'app/shared/section-card';
import styles from './domain-card.module.css';

const extractMetric = (metrics = {}, key) => {
  const raw = metrics[key];
  if (raw === null || raw === undefined) return { value: null, trend: null };
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return { value: raw.value ?? null, trend: raw.trend ?? null };
  }
  return { value: raw, trend: null };
};

const formatValue = (value, format) => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (isNaN(n)) return '—';

  switch (format) {
    case 'percent':
      return `${n.toFixed(1)}%`;
    case 'currency':
      return n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n.toFixed(0)}`;
    case 'ms':
      return `${Math.round(n)}ms`;
    case 'score':
      return `${Math.round(n)}/100`;
    case 'health':
      return `${Math.round(n)}% healthy`;
    case 'count':
      if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
      if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
      return `${Math.round(n)}`;
    default:
      if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
      if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
      return `${Math.round(n)}`;
  }
};

const TrendArrow = ({ trend, invert }) => {
  if (!trend || trend === 'flat') return null;
  const is_up = trend === 'up' || Number(trend) > 0;
  const is_positive = invert ? !is_up : is_up;
  return (
    <span className={is_positive ? styles.trendUp : styles.trendDown}>
      <Icon name={is_up ? 'arrow_up' : 'arrow_down'} width={9} height={9} />
    </span>
  );
};

export const DomainCard = ({
  title,
  icon,
  headline,
  supporting,
  metrics = {},
  anomaly_count = 0,
  updated_at
}) => {
  const headline_metric = extractMetric(metrics, headline.metric);
  const last_analysed = updated_at
    ? new Date(updated_at).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      })
    : null;

  const card_title = (
    <>
      <span className={styles.icon}>
        <Icon name={icon} width={14} height={14} />
      </span>
      {title}
    </>
  );

  const card_action =
    anomaly_count > 0 ? (
      <span className={styles.anomalyBadge}>
        {anomaly_count} {anomaly_count === 1 ? 'anomaly' : 'anomalies'}
      </span>
    ) : null;

  return (
    <SectionCard title={card_title} action={card_action}>
      {last_analysed && <div className={styles.timestamp}>{last_analysed}</div>}
      <div className={styles.headline}>
        <div className={styles.headlineValue}>
          {formatValue(headline_metric.value, headline.format)}
          {headline.trend && <TrendArrow trend={headline_metric.trend} invert={headline.invert} />}
        </div>
        <div className={styles.headlineLabel}>{headline.label}</div>
      </div>
      <div className={styles.supporting}>
        {supporting.map((s) => {
          const m = extractMetric(metrics, s.metric);
          return (
            <div key={s.metric} className={styles.supportingRow}>
              <span className={styles.supportingLabel}>{s.label}</span>
              <span
                className={`${styles.supportingValue} ${
                  s.danger && m.value > 0 ? styles.danger : ''
                } ${s.warn && m.value > 0 ? styles.warn : ''}`}
              >
                {s.trend && <TrendArrow trend={m.trend} invert={s.invert} />}
                {formatValue(m.value, s.format)}
              </span>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
};
